import React, { useEffect, useState } from 'react';

interface ConnectionStabilityIndicatorProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

// CSS for animation
const pulseAnimation = `
  @keyframes pulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
  }
`;

/**
 * ConnectionStabilityIndicator Component
 * 
 * Shows a subtle indicator when connection stability is being enforced
 * to prevent excessive WebSocket connections
 */
const ConnectionStabilityIndicator: React.FC<ConnectionStabilityIndicatorProps> = ({ 
  position = 'bottom-right' 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [stabilityEndTime, setStabilityEndTime] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  
  // Listen for connection stability events
  useEffect(() => {
    const handleStabilityEnforced = (event: CustomEvent) => {
      console.log('Connection stability enforced:', event.detail);
      setIsVisible(true);
      setStabilityEndTime(event.detail.until || Date.now() + 60000);
    };
    
    // Add event listener
    window.addEventListener('connectionStabilityEnforced', handleStabilityEnforced as EventListener);
    
    // Add animation styles
    const styleEl = document.createElement('style');
    styleEl.innerHTML = pulseAnimation;
    document.head.appendChild(styleEl);
    
    return () => {
      window.removeEventListener('connectionStabilityEnforced', handleStabilityEnforced as EventListener);
      
      // Remove animation styles
      if (styleEl && document.head.contains(styleEl)) {
        document.head.removeChild(styleEl);
      }
    };
  }, []);
  
  // Update countdown timer
  useEffect(() => {
    if (!isVisible || !stabilityEndTime) return;
    
    const intervalId = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, stabilityEndTime - now);
      
      if (remaining <= 0) {
        setIsVisible(false);
        setStabilityEndTime(null);
        clearInterval(intervalId);
      } else {
        setTimeRemaining(Math.ceil(remaining / 1000));
      }
    }, 1000);
    
    return () => clearInterval(intervalId);
  }, [isVisible, stabilityEndTime]);
  
  // Not visible if we're not enforcing stability
  if (!isVisible) return null;
  
  // Position styles
  const positionStyles = {
    'top-right': { top: '10px', right: '10px' },
    'top-left': { top: '10px', left: '10px' },
    'bottom-right': { bottom: '10px', right: '10px' },
    'bottom-left': { bottom: '10px', left: '10px' }
  };
  
  return (
    <div 
      style={{
        position: 'fixed',
        ...positionStyles[position],
        zIndex: 1000,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '4px',
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
        maxWidth: '250px',
        transition: 'opacity 0.3s ease',
        opacity: 0.9
      }}
    >
      <div
        style={{
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          backgroundColor: '#ff9800',
          marginRight: '8px',
          animation: 'pulse 2s infinite'
        }}
      />
      <div>
        Stabilizing connection...
        <div style={{ fontSize: '10px', opacity: 0.8 }}>
          Auto-resume in {timeRemaining}s
        </div>
      </div>
    </div>
  );
};

export default ConnectionStabilityIndicator; 